import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, historyToString, buildCombinedReason } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfLabelValue, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';
import { PdfExamDetails, PdfMaternalSections, PdfMaternalMeasurements } from '../utils/PdfMaternalSections';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isUADopplerAbnormal(d) {
  if (!d || typeof d !== 'object') return false;
  return d.diastolicFlow === 'Absent' || d.diastolicFlow === 'Reversed';
}

// Renders a UA Doppler row with diastolic flow in red when abnormal
function UADopplerRow({ label, d }) {
  const sdText    = d.sdRatio ? (d.percentile ? `S/D: ${d.sdRatio} (${d.percentile}%)` : `S/D: ${d.sdRatio}`) : '';
  const psvText   = d.psv ? `PSV: ${d.psv} cm/s` : '';
  const flowLabel = d.diastolicFlow === 'Present'  ? 'Diastolic flow (+)'
                  : d.diastolicFlow === 'Absent'   ? 'Diastolic flow (-)'
                  : d.diastolicFlow === 'Reversed' ? 'Diastolic flow (-)'
                  : '';
  const flowAbnormal = isUADopplerAbnormal(d);
  const prefix = [sdText, psvText].filter(Boolean).join(', ');

  return (
    <View style={commonStyles.row}>
      <Text style={commonStyles.label}>{label}</Text>
      <Text style={commonStyles.valueDefault}>
        {prefix ? `${prefix}, ` : ''}
        <Text style={flowAbnormal ? { color: COLORS.abnormal } : {}}>{flowLabel}</Text>
      </Text>
    </View>
  );
}

// A UA Doppler field is considered filled only if the user entered actual
// measurements or changed diastolic flow from the default 'Present'.
function isUADopplerFilled(d) {
  if (!d || typeof d !== 'object') return false;
  return !!(d.psv || d.sdRatio || d.percentile || (d.diastolicFlow && d.diastolicFlow !== 'Present'));
}

function formatEGA(ega) {
  if (!ega) return '';
  if (typeof ega === 'string') return ega;
  const w = (ega.weeks !== undefined && ega.weeks !== '') ? ega.weeks : null;
  const d = (ega.days  !== undefined && ega.days  !== '') ? ega.days  : null;
  if (w === null && d === null) return '';
  return [w !== null ? `${w}w` : null, d !== null ? `${d}d` : null].filter(Boolean).join(' ');
}

function formatBioMeasure(field, label) {
  const obj = field && typeof field === 'object' ? field : null;
  const size = obj ? obj.size : field;
  if (!size) return null;
  const w = obj && obj.weeks !== '' && obj.weeks !== undefined ? obj.weeks : null;
  const d = obj && obj.days  !== '' && obj.days  !== undefined ? obj.days  : null;
  let suffix = '';
  if (w !== null || d !== null) {
    const parts = [];
    if (w !== null) parts.push(`${w}w`);
    if (d !== null) parts.push(`${d}d`);
    suffix = ` (${parts.join(' ')})`;
  }
  const pct = obj && obj.percentile ? `, ${obj.percentile}%` : '';
  return `${label}: ${size} cm${suffix}${pct}`;
}

// BPP: returns { scoreText, isAbnormal } or null if no fields filled
const BPP_NORMAL  = { breathing: 'Present', movement: 'Present', tone: 'Present', fluid: 'Adequate' };
const BPP_ABSENT_LABEL = {
  breathing: 'No fetal breathing',
  movement:  'No fetal movement',
  tone:      'No fetal tone',
  fluid:     'Decreased amniotic fluid',
};

function formatBPP(bpp) {
  const fields = ['breathing', 'movement', 'tone', 'fluid'];
  const filled = fields.filter(f => bpp[f] !== '');
  if (filled.length === 0) return null;

  const score = filled.filter(f => bpp[f] === BPP_NORMAL[f]).length * 2;
  const maxScore = 8;
  const abnormalFields = filled.filter(f => bpp[f] !== BPP_NORMAL[f]);
  const isAbnormal = score < maxScore;

  let text = `BPP: ${score}/${maxScore}`;
  if (abnormalFields.length > 0) {
    text += ` (${abnormalFields.map(f => BPP_ABSENT_LABEL[f]).join(', ')})`;
  }
  return { text, isAbnormal };
}

// Anatomy summary
const ANATOMY_LABELS = {
  nasalBone:     'Nasal Bone',
  profile:       'Profile',
  noseLips:      'Nose/Lips',
  orbits:        'Orbits',
  ventricles:    'Ventricles',
  choroidPlexus: 'Choroid Plexus',
  midlineFalx:   'Midline Falx',
  csp:           'CSP',
  cerebellum:    'Cerebellum',
  cisternaMagna: 'Cisterna Magna',
  nuchalFold:    'Nuchal Fold',
  fetalCI:       'Fetal CI',
  placentaCI:    'Plac CI',
  bladder:       'Bladder',
  kidneys:       'Kidneys',
  openHands:     'Open Hands',
  arms:          'Arms',
  feetAnkle:     'Feet/Ankle',
  legs:          'Legs',
  diaphragm:     'Diaphragm',
  stomach:       'Stomach',
  cSpine:        'C-Spine',
  tSpine:        'T-Spine',
  lSpine:        'L-Spine',
  sSpine:        'S-Spine',
  fourCH:        '4CH',
  lvot:          'LVOT',
  rvot:          'RVOT',
  threeVV:       '3VV',
  threeCord:     '3 Cord',
  situs:         'Situs',
};

function buildAnatomySummary(anatomy) {
  const abnormal = [];
  const notWellViz = [];
  let hasEvaluated = false;

  Object.entries(ANATOMY_LABELS).forEach(([key, label]) => {
    const val = anatomy[key];
    if (!val || val === 'Not Evaluated') return;
    hasEvaluated = true;
    if (val === 'Abnormal') abnormal.push(label);
    else if (val === 'Not Well Visualized') notWellViz.push(label);
  });

  if (!hasEvaluated) return null;
  return { abnormal, notWellViz, allNormal: abnormal.length === 0 && notWellViz.length === 0 };
}

// ---------------------------------------------------------------------------
// FetalPage
// ---------------------------------------------------------------------------

function FetalPage({ fetus, index, data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);
  const ga = fetus.gestationalAge || {};
  const bio = fetus.biometry || {};
  const bpp = fetus.biophysicalProfile || {};
  const anatomy = fetus.anatomy || {};
  const uaDoppler = fetus.uaDoppler || {};
  const ms = data.maternalStructures || {};
  const label = String.fromCharCode(65 + index);
  const title = `2ND TRIMESTER OB — FETUS ${label}`;

  const measurementsText = [
    fetus.fetalHeartRate ? `FHR: ${fetus.fetalHeartRate} bpm` : null,
    fetus.afi            ? `AFI: ${fetus.afi} cm`             : null,
    formatBioMeasure(bio.bpd, 'BPD'),
    formatBioMeasure(bio.hc,  'HC'),
    formatBioMeasure(bio.ac,  'AC'),
    formatBioMeasure(bio.fl,  'FL'),
    bio.efw ? (bio.percentile ? `EFW: ${bio.efw} g (${bio.percentile}%)` : `EFW: ${bio.efw} g`) : null,
    bio.ci    ? `CI: ${bio.ci}`       : null,
    bio.hcac  ? `HC/AC: ${bio.hcac}`  : null,
    bio.flbpd ? `FL/BPD: ${bio.flbpd}` : null,
    bio.flhc  ? `FL/HC: ${bio.flhc}`  : null,
    bio.flac  ? `FL/AC: ${bio.flac}`  : null,
  ].filter(Boolean).join('\n');

  const isMultiple     = (data.fetuses || []).length > 1;
  const bppResult      = formatBPP(bpp);
  const anatomySummary = buildAnatomySummary(anatomy);
  const fhrAbnormal    = !!(fetus.fetalHeartRate && (parseFloat(fetus.fetalHeartRate) < 110 || parseFloat(fetus.fetalHeartRate) > 160));
  const cordAbnormal   = fetus.umbilicalCord === '2 vessel cord';
  const previaAbnormal = !!(fetus.placentaPrevia && fetus.placentaPrevia !== 'None');
  const showUADoppler  = isUADopplerFilled(uaDoppler.fetal) || isUADopplerFilled(uaDoppler.midCord) || isUADopplerFilled(uaDoppler.placenta);

  return (
    <Page size="LETTER" style={commonStyles.page}>
      <PdfPageHeader facilityInfo={facilityInfo} examTitle={title} />
      <PdfPatientBox
        firstName={data.firstName}
        lastName={data.lastName}
        mrn={data.mrn}
        examDate={data.examDate}
        technologist={data.technologist}
        studentWorksheet={data.studentWorksheet}
        supervisingSonographer={data.supervisingSonographer}
        reason={reason}
      />
      <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

      <View style={commonStyles.twoCol}>

        {/* ── LEFT COLUMN ── */}
        <View style={commonStyles.colLeft}>

          {/* Biometry box */}
          <PdfAccentSectionHeader title="MEASUREMENTS" />
          <View style={commonStyles.measurementBox}>
            <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>
              {isMultiple ? `Biometry (Fetus ${label})` : 'Biometry'}
            </Text>
            <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
          </View>

          {/* Gestational Age */}
          <View style={commonStyles.measurementBox}>
            <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>
              {isMultiple ? `Gestational Age (Fetus ${label})` : 'Gestational Age'}
            </Text>
            {ga.lmp ? <Text style={commonStyles.infoBoxText}>LMP: {ga.lmp}</Text> : null}
            {formatEGA(ga.clinicalEGA) ? <Text style={commonStyles.infoBoxText}>Clinical EGA: {formatEGA(ga.clinicalEGA)}</Text> : null}
            {ga.clinicalEDD ? <Text style={commonStyles.infoBoxText}>Clinical EDD: {ga.clinicalEDD}</Text> : null}
            {formatEGA(ga.ultrasoundEGA) ? <Text style={commonStyles.infoBoxText}>US EGA: {formatEGA(ga.ultrasoundEGA)}</Text> : null}
            {ga.ultrasoundEDD ? <Text style={commonStyles.infoBoxText}>US EDD: {ga.ultrasoundEDD}</Text> : null}
          </View>

          <PdfMaternalMeasurements maternal={ms} />

          {/* BPP — blue box, only when filled */}
          {bppResult ? (
            <View style={commonStyles.measurementBox}>
              <Text style={[
                commonStyles.infoBoxText,
                { fontFamily: 'Helvetica-Bold' },
                bppResult.isAbnormal ? { color: COLORS.abnormal } : {},
              ]}>
                {bppResult.text}
              </Text>
            </View>
          ) : null}

          {/* UA Doppler — blue box, only when filled */}
          {showUADoppler ? (
            <View style={commonStyles.measurementBox}>
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>UA Doppler</Text>
              {isUADopplerFilled(uaDoppler.fetal)    ? <UADopplerRow label="Fetal:"    d={uaDoppler.fetal}    /> : null}
              {isUADopplerFilled(uaDoppler.midCord)  ? <UADopplerRow label="Mid-Cord:" d={uaDoppler.midCord}  /> : null}
              {isUADopplerFilled(uaDoppler.placenta) ? <UADopplerRow label="Placenta:" d={uaDoppler.placenta} /> : null}
            </View>
          ) : null}

        </View>

        {/* ── RIGHT COLUMN ── */}
        <View style={commonStyles.colRight}>

          <PdfExamDetails examTypeDetails={data.examTypeDetails} />

          {/* Fetal Status */}
          <PdfAccentSectionHeader title="FETAL STATUS" />
          {fetus.fetalHeartRate ? (
            <PdfLabelValue label="Fetal heart rate:" value={`${fetus.fetalHeartRate} bpm`}
              isAbnormal={fhrAbnormal} forceBlack={!fhrAbnormal} />
          ) : null}
          <PdfLabelValue label="Presentation:" value={fetus.presentation || ''} forceBlack />
          {isMultiple && fetus.maternalLocation ? <PdfLabelValue label="Fetal location:" value={fetus.maternalLocation} forceBlack /> : null}
          {isMultiple && data.chorionicity ? <PdfLabelValue label="Chorionicity:" value={data.chorionicity} forceBlack /> : null}

          {/* Placenta / Fluid */}
          <PdfAccentSectionHeader title="PLACENTA / FLUID" />
          <PdfLabelValue label="Placenta:" value={
            fetus.placentaLocation
              ? (fetus.placentaGrade !== '' && fetus.placentaGrade !== undefined
                  ? `${fetus.placentaLocation} (grade ${fetus.placentaGrade})`
                  : fetus.placentaLocation)
              : ''
          } forceBlack />
          <PdfLabelValue label="Placenta previa:" value={fetus.placentaPrevia || 'None'} isAbnormal={previaAbnormal} forceBlack={!previaAbnormal} />
          <PdfLabelValue label="Umbilical cord:" value={fetus.umbilicalCord || ''} isAbnormal={cordAbnormal} forceBlack={!cordAbnormal} />
          {fetus.amnioticFluid ? (
            <PdfLabelValue label="Amniotic fluid:" value={fetus.amnioticFluid}
              isAbnormal={fetus.amnioticFluid !== 'Normal'}
              forceBlack={fetus.amnioticFluid === 'Normal'} />
          ) : null}

          <PdfMaternalSections maternal={ms} />

          {/* Anatomy — below Maternal Structures */}
          {anatomySummary ? (
            <>
              <PdfAccentSectionHeader title="ANATOMY" />
              {anatomySummary.allNormal ? (
                <Text style={commonStyles.valueDefault}>All anatomic structures visualized and appear normal.</Text>
              ) : (
                <>
                  {anatomySummary.abnormal.length > 0 ? (
                    <Text style={[commonStyles.valueDefault, { color: COLORS.abnormal }]}>
                      Abnormal Structures: {anatomySummary.abnormal.join(', ')}
                    </Text>
                  ) : null}
                  {anatomySummary.notWellViz.length > 0 ? (
                    <Text style={commonStyles.valueDefault}>
                      Not Well Visualized: {anatomySummary.notWellViz.join(', ')}
                    </Text>
                  ) : null}
                  <Text style={commonStyles.valueDefault}>All other visualized anatomic structures appear normal.</Text>
                </>
              )}
            </>
          ) : null}

          {/* Comments */}
          <PdfCommentsBox comments={fetus.comments || data.comments} />

        </View>

      </View>

      <PdfFooter facilityInfo={facilityInfo} />
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export function SecondTrimesterMultipleOBPDF({ data, facilityInfo }) {
  const fetuses = data.fetuses || [];

  return (
    <Document>
      {fetuses.length > 0 ? fetuses.map((fetus, i) => (
        // onlyFetusIndex: PNG output renders one fetus per document so pages can be named per fetus
        data.onlyFetusIndex == null || data.onlyFetusIndex === i
          ? <FetalPage key={i} fetus={fetus} index={i} data={data} facilityInfo={facilityInfo} />
          : null
      )) : (
        <FetalPage fetus={{}} index={0} data={data} facilityInfo={facilityInfo} />
      )}
    </Document>
  );
}
